import { useCallback, useState } from "react"
import { useSessionStore } from "@/store/sessionStore"
import { describeWriteError } from "@/utils/firestoreWrite"

/**
 * Fire-and-forget writes use the listener for UI updates.
 * Submit is for operations whose result is needed by the next step.
 *
 * Both report whether a write is still out, because a button that has been
 * pressed and shows nothing reads as a button that did not work. The spinner is
 * the only thing on screen that says so: Firestore applies a write locally
 * before the server has acknowledged it, so the round usually changes first and
 * the promise settles afterwards.
 *
 * Which is also why nothing here times out. The pending flag clears when the
 * server answers, and while it has not — offline, or on a connection that has
 * gone quiet — the write genuinely has not landed. A timeout would report it as
 * finished.
 */

interface FirestoreWrite {
  /**
   * Fire a write. Pass `key` to have `isPending` track it: one key per button,
   * so a screen with five of them does not spin all five on one press. Omit it
   * where nothing is watching — a debounced text field, say.
   */
  write: (operation: () => Promise<unknown>, key?: string) => void
  /** True while a write fired under `key` is still out. */
  isPending: (key: string) => boolean
}

/** Fire a write without blocking the UI. */
export function useFirestoreWrite(): FirestoreWrite {
  const setError = useSessionStore((s) => s.setError)
  /*
   * A count per key rather than a flag. The same button can be pressed twice
   * before either write lands — the timer's ± is used exactly like that — and a
   * flag would clear on the first answer while the second was still out.
   */
  const [inFlight, setInFlight] = useState<Record<string, number>>({})

  const track = useCallback((key: string, delta: number) => {
    setInFlight((current) => {
      const next = { ...current, [key]: (current[key] ?? 0) + delta }
      if (next[key] <= 0) delete next[key]
      return next
    })
  }, [])

  /*
   * Stable across renders even though the pending state is not: `write` is a
   * dependency of the flush callbacks in `KnownPainsModal`, and a new identity
   * per keystroke there would tear down the debounce it is meant to hold.
   */
  const write = useCallback(
    (operation: () => Promise<unknown>, key?: string) => {
      if (key !== undefined) track(key, 1)
      void operation()
        .catch((error: unknown) => setError(describeWriteError(error)))
        .finally(() => {
          if (key !== undefined) track(key, -1)
        })
    },
    [setError, track]
  )

  const isPending = useCallback((key: string) => (inFlight[key] ?? 0) > 0, [inFlight])

  return { write, isPending }
}

interface FirestoreSubmit<T> {
  submit: (operation: () => Promise<T>) => Promise<T | null>
  pending: boolean
  error: string | null
  clearError: () => void
}

/** Await a write when its result is required by the next step. */
export function useFirestoreSubmit<T>(): FirestoreSubmit<T> {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = useCallback(async (operation: () => Promise<T>): Promise<T | null> => {
    setPending(true)
    setError(null)
    try {
      return await operation()
    } catch (caught: unknown) {
      setError(describeWriteError(caught))
      return null
    } finally {
      setPending(false)
    }
  }, [])

  const clearError = useCallback(() => setError(null), [])

  return { submit, pending, error, clearError }
}
